import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getProjectsPaginated } from '../../services/project';
import { getProjectReport } from '../../services/reports';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';

const BranchWiseSummary = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    branch: '',
    searchTerm: ''
  });
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);

  // Helper function to format currency amounts with proper Indian number formatting
  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    });
  };

  // Helper function to format regular numbers with Indian formatting
  const formatNumber = (number) => {
    return Number(number || 0).toLocaleString('en-IN');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchReportData(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchReportData(1, newRowsPerPage);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch projects for dropdown
  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await getProjectsPaginated({ page: 1, limit: 100 });
      const list = Array.isArray(response?.data) ? response.data : [];
      const formatted = list.map((project) => ({
        id: project._id || project.id,
        name: project.projectName || project.name || 'Untitled Project',
        projectNo: project.projectNo || '',
      }));
      setProjects(formatted);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Fetch report data
  const fetchReportData = useCallback(async (pageNum = 1, limit = rowsPerPage) => {
    setLoadingReport(true);
    try {
      const params = { page: pageNum, limit };
      if (filters.branch) {
        params.projectId = filters.branch;
      }
      if (filters.fromDate) {
        params.fromDate = filters.fromDate;
      }
      if (filters.toDate) {
        params.toDate = filters.toDate;
      }

      const response = await getProjectReport(params);
      if (response?.success && Array.isArray(response.data)) {
        const mapped = response.data.map((item, index) => ({
          sno: (pageNum - 1) * limit + index + 1,
          branchName: item.projectName || 'N/A',
          totalBookings: item.totalBookings ?? 0,
          totalAmount: item.totalBookingAmount ?? 0,
          paidAmount: item.totalPaidAmount ?? 0,
          dueAmount: item.totalDueAmount ?? 0,
          completedBookings: item.bookedInventory ?? 0,
          pendingBookings: item.availableInventory ?? 0,
        }));
        setReportData(mapped);
        setTotalProjects(response.pagination?.totalProjects || mapped.length);
      } else {
        setReportData([]);
        setTotalProjects(0);
      }
    } catch (error) {
      console.error('Error fetching project report:', error);
      setReportData([]);
      setTotalProjects(0);
    } finally {
      setLoadingReport(false);
    }
  }, [filters.branch, filters.fromDate, filters.toDate, rowsPerPage]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Load report data on mount and whenever filters change
  useEffect(() => {
    fetchReportData(1, rowsPerPage);
    setPage(0);
  }, [fetchReportData]);

  const handleSearch = () => {
    // Reset to first page whenever a manual search is triggered
    setPage(0);
    fetchReportData(1, rowsPerPage);
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      branch: '',
      searchTerm: ''
    });
    setPage(0);
    // Use the empty filters for a fresh fetch
    // Since fetchReportData depends on filters, and we use it inside handleReset,
    // it's safer to pass the default values or rely on a separate call if needed.
    // However, the existing setFilters is async.
    // Let's call fetchReportData with manual params.
    setLoadingReport(true);
    const params = { page: 1, limit: rowsPerPage };
    getProjectReport(params).then(response => {
      if (response?.success && Array.isArray(response.data)) {
        const mapped = response.data.map((item, index) => ({
          sno: index + 1,
          branchName: item.projectName || 'N/A',
          totalBookings: item.totalBookings ?? 0,
          totalAmount: item.totalBookingAmount ?? 0,
          paidAmount: item.totalPaidAmount ?? 0,
          dueAmount: item.totalDueAmount ?? 0,
          completedBookings: item.bookedInventory ?? 0,
          pendingBookings: item.availableInventory ?? 0,
        }));
        setReportData(mapped);
        setTotalProjects(response.pagination?.totalProjects || mapped.length);
      } else {
        setReportData([]);
        setTotalProjects(0);
      }
    }).catch(err => {
      console.error(err);
      setReportData([]);
    }).finally(() => setLoadingReport(false));
  };

  // Export functions
  const buildExportRows = () => {
    return reportData.map((item) => ({
      'S.No': item.sno,
      'Branch Name': item.branchName,
      'Total Bookings': item.totalBookings,
      'Total Amount (Rs.)': item.totalAmount,
      'Paid Amount (Rs.)': item.paidAmount,
      'Due Amount (Rs.)': item.dueAmount,
      'Completed': item.completedBookings,
      'Pending': item.pendingBookings
    }));
  };

  const getExportFileName = () => `branch_wise_summary_${new Date().toISOString().split('T')[0]}`;

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: getExportFileName() });
  };

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    
    // Add total row for PDF
    const rowsForPdf = [...rows, {
      'S.No': '',
      'Branch Name': 'Total',
      'Total Bookings': totals.bookings,
      'Total Amount (Rs.)': `Rs. ${formatCurrency(totals.amount)}`,
      'Paid Amount (Rs.)': `Rs. ${formatCurrency(totals.paid)}`,
      'Due Amount (Rs.)': `Rs. ${formatCurrency(totals.due)}`,
      'Completed': '',
      'Pending': ''
    }];

    // Format currencies properly
    const formattedRows = rowsForPdf.map(r => {
      // Don't double format the totals row
      if (r['Branch Name'] === 'Total') return r;
      
      return {
        ...r,
        'Total Bookings': formatNumber(r['Total Bookings']),
        'Total Amount (Rs.)': `Rs. ${formatCurrency(r['Total Amount (Rs.)'])}`,
        'Paid Amount (Rs.)': `Rs. ${formatCurrency(r['Paid Amount (Rs.)'])}`,
        'Due Amount (Rs.)': `Rs. ${formatCurrency(r['Due Amount (Rs.)'])}`,
        'Completed': formatNumber(r['Completed']),
        'Pending': formatNumber(r['Pending'])
      };
    });

    exportAsPDF({
      rows: formattedRows,
      title: 'Branch Wise Summary Report',
      fileNameBase: getExportFileName(),
      orientation: 'l'
    });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    
    // Add total row for Excel
    const rowsForExcel = [...rows, {
      'S.No': '',
      'Branch Name': 'Total',
      'Total Bookings': totals.bookings,
      'Total Amount (Rs.)': totals.amount,
      'Paid Amount (Rs.)': totals.paid,
      'Due Amount (Rs.)': totals.due,
      'Completed': '',
      'Pending': ''
    }];

    exportAsExcel({
      rows: rowsForExcel,
      sheetName: 'Branch Summary',
      fileNameBase: getExportFileName()
    });
  };

  const totals = {
    bookings: reportData.reduce((sum, item) => sum + Number(item.totalBookings || 0), 0),
    amount: reportData.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0),
    paid: reportData.reduce((sum, item) => sum + Number(item.paidAmount || 0), 0),
    due: reportData.reduce((sum, item) => sum + Number(item.dueAmount || 0), 0),
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            Branch Wise Summary
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive overview of branch performance and financial metrics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <ExportButton 
            onExportPdf={handleExportPdf} 
            onExportCsv={handleExportCsv} 
            onExportExcel={handleExportExcel} 
          />
          <RefreshButton
            onClick={() => fetchReportData(page + 1, rowsPerPage)}
            loading={loadingReport}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'primary.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(filteredData.length)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Branches
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'error.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(totals.bookings)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Bookings
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'info.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    ₹{formatCurrency(totals.paid)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Paid Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'success.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    ₹{formatCurrency(totals.due)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Due Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#536DFE' }}>
            Project Filters
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="project-select-label" shrink>Project</InputLabel>
                <Select
                  id="project-select"
                  labelId="project-select-label"
                  value={filters.branch}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  label="Project"
                  sx={{
                    borderRadius: 2,
                  }}
                  disabled={loadingProjects}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return 'All Projects';
                    const project = projects.find((p) => p.id === value);
                    return project
                      ? `${project.name}${project.projectNo ? ` (#${project.projectNo})` : ''}`
                      : 'All Projects';
                  }}
                >
                  <MenuItem value="">
                    <em>All Projects</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}{project.projectNo ? ` (#${project.projectNo})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Search branch field temporarily disabled */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search Branch"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid> */}
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    py: 1,
                    height: '40px'
                  }}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    py: 1,
                    height: '40px'
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#536DFE' }}>
            Project Summary Details
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Project Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Bookings</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Amount (Rs.)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Paid Amount (Rs.)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Due Amount (Rs.)</TableCell>
                  {/* <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Completed</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Pending</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingReport ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Loading report...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : reportData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No project data found for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.map((row) => (
                    <TableRow
                      key={row.sno}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.branchName}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#1976d2' }}>
                        {formatNumber(row.totalBookings)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#2e7d32' }}>
                        Rs. {formatCurrency(row.totalAmount)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#1976d2' }}>
                        Rs. {formatCurrency(row.paidAmount)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#d32f2f' }}>
                        Rs. {formatCurrency(row.dueAmount)}
                      </TableCell>
                      {/* <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                        {formatNumber(row.completedBookings)}
                      </TableCell> */}
                      {/* <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                        {formatNumber(row.pendingBookings)}
                      </TableCell> */}
                    </TableRow>
                  ))
                )}
                {reportData.length > 0 && !loadingReport && (
                  <TableRow sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '16px' }}>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#1976d2' }}>
                      <strong>{formatNumber(totals.bookings)}</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#2e7d32' }}>
                      <strong>Rs. {formatCurrency(totals.amount)}</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#1976d2' }}>
                      <strong>Rs. {formatCurrency(totals.paid)}</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#d32f2f' }}>
                      <strong>Rs. {formatCurrency(totals.due)}</strong>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            
            component="div"
            count={totalProjects}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                backgroundColor: '#f8f9fa',
              }
            }}
          />
        </CardContent>
      </Card>


    </Box>
  );
};

export default BranchWiseSummary;

